/**
 * EAA-2029: client-side guard for AI chat edits in "Trình tạo AI".
 *
 * One chat message once turned a 5-question English set into 1 Vietnamese question
 * with no way back. The chat now sends a targeted edit, keeps the pre-edit content in
 * memory for a one-step undo, and compares before/after so a dropped question or a
 * language switch is flagged even if the server-side guards miss it.
 */

export type ChatEditMode = 'patch' | 'regenerate';

export interface ChatEditSnapshot {
    title?: string;
    data: Record<string, unknown>;
    config?: Record<string, unknown> | null;
}

export interface ChatEditRejection {
    ref: string;
    item_index?: number | null;
    reason: string;
}

/** `result` of a chat-edit / regenerate job, as far as the chat needs it. */
export interface ChatEditJobResult {
    edit_mode?: ChatEditMode;
    saved?: boolean;
    changed_items?: number[];
    rejected?: ChatEditRejection[];
    regenerate_requested?: boolean;
    summary?: string;
}

export interface ChatEditAssessment {
    countBefore: number;
    countAfter: number;
    droppedItems: boolean;
    languageBefore: 'vi' | 'en' | null;
    languageAfter: 'vi' | 'en' | null;
    languageChanged: boolean;
    changed: boolean;
}

type AnyRecord = Record<string, unknown>;

const VI_CHARS = new Set(
    'àáảãạăằắẳẵặâầấẩẫậđèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵ'
);

const asRecord = (value: unknown): AnyRecord | null =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as AnyRecord) : null;

/** Sub-question list of a saved set (`questionSet.items`), or null for other shapes. */
export function questionSetItems(data: unknown): AnyRecord[] | null {
    const items = asRecord(asRecord(data)?.questionSet)?.items;
    return Array.isArray(items) ? (items as AnyRecord[]) : null;
}

export function questionSetItemCount(data: unknown): number {
    return questionSetItems(data)?.length ?? 0;
}

const blockText = (block: unknown): string => {
    const b = asRecord(block);
    return b && typeof b.text === 'string' ? b.text : '';
};

/** Learner-facing text of the set (stems + choices), explanations excluded. */
export function questionSetBodyText(data: unknown): string {
    const parts: string[] = [];
    for (const item of questionSetItems(data) ?? []) {
        const body = asRecord(item.data);
        if (!body) continue;
        parts.push(blockText(body.question));
        for (const choice of (Array.isArray(body.choices) ? body.choices : []) as unknown[]) {
            parts.push(blockText(asRecord(choice)?.content));
        }
    }
    return parts.join(' ');
}

/** Same heuristic as the Agent guard: share of words carrying Vietnamese letters. */
export function detectLanguage(text: string): 'vi' | 'en' | null {
    const words = (text || '').normalize('NFC').toLowerCase().match(/\p{L}+/gu) ?? [];
    if (words.length < 2) return null;
    const vi = words.filter((w) => [...w].some((ch) => VI_CHARS.has(ch))).length;
    const ratio = vi / words.length;
    if (ratio >= 0.2) return 'vi';
    if (ratio <= 0.05 && words.length >= 3) return 'en';
    return null;
}

export function assessChatEdit(before: unknown, after: unknown): ChatEditAssessment {
    const countBefore = questionSetItemCount(before);
    const countAfter = questionSetItemCount(after);
    const languageBefore = detectLanguage(questionSetBodyText(before));
    const languageAfter = detectLanguage(questionSetBodyText(after));
    return {
        countBefore,
        countAfter,
        droppedItems: countBefore > 0 && countAfter < countBefore,
        languageBefore,
        languageAfter,
        languageChanged: !!languageBefore && !!languageAfter && languageBefore !== languageAfter,
        changed: JSON.stringify(before ?? null) !== JSON.stringify(after ?? null),
    };
}

/** True when the message plainly asks to rebuild the whole set (mirrors the Agent). */
export function wantsWholeSetRegeneration(message: string): boolean {
    return /(?:tạo|sinh|làm|viết)\s+lại\s+(?:toàn\s+bộ|cả\s+bộ|tất\s+cả|từ\s+đầu|hết)|(?:tạo|sinh)\s+(?:một\s+)?bộ\s+(?:câu\s+hỏi\s+)?mới|regenerate\s+(?:all|everything|the\s+(?:whole|entire)|every)|start\s+over|from\s+scratch/iu.test(
        (message || '').normalize('NFC')
    );
}

/** Build the additive `edit` object for `POST /content/{id}/regenerate`. */
export function buildChatEdit(
    mode: ChatEditMode,
    targets: number[]
): { mode: ChatEditMode; target_items?: number[] } {
    return mode === 'patch' && targets.length > 0
        ? { mode, target_items: [...targets].sort((a, b) => a - b) }
        : { mode };
}
